import { Bot, User } from "lucide-react";
import type { ClientAgentMessage } from "@/types/agent";
import { MarkdownContent } from "./markdown-content";
import { ProposalCard } from "./proposal-card";
import { SuggestionChips } from "./suggestion-chips";
import { ToolCallDisplay } from "./tool-call-display";

interface MessageBubbleProps {
  message: ClientAgentMessage;
  isStreaming?: boolean;
  isLastAssistant?: boolean;
  onApproveProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onSuggestionClick: (text: string) => void;
}

export function MessageBubble({
  message,
  isStreaming,
  isLastAssistant,
  onApproveProposal,
  onRejectProposal,
  onSuggestionClick,
}: MessageBubbleProps): React.ReactElement {
  const isUser = message.role === "user";

  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
          isUser ? "bg-primary text-primary-foreground" : "bg-muted"
        }`}
      >
        {isUser ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
      </div>

      <div className={`max-w-[85%] space-y-1 ${isUser ? "items-end" : "items-start"}`}>
        {message.toolCalls && message.toolCalls.length > 0 && <ToolCallDisplay toolCalls={message.toolCalls} />}

        {message.content && (
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              message.isError
                ? "bg-destructive/10 text-destructive border border-destructive/20"
                : isUser
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
            }`}
          >
            {isUser ? (
              <p className="whitespace-pre-wrap break-words">{message.content}</p>
            ) : (
              <MarkdownContent content={message.content} />
            )}
            {isStreaming && !isUser && (
              <span className="inline-block w-1.5 h-4 ml-0.5 bg-foreground/50 animate-pulse" />
            )}
          </div>
        )}

        {!message.content && isStreaming && !isUser && (
          <div className="rounded-lg bg-muted px-3 py-2">
            <span className="inline-block w-1.5 h-4 bg-foreground/50 animate-pulse" />
          </div>
        )}

        {message.proposal && (
          <ProposalCard proposal={message.proposal} onApprove={onApproveProposal} onReject={onRejectProposal} />
        )}

        {isLastAssistant && !isStreaming && message.suggestions && message.suggestions.length > 0 && (
          <SuggestionChips suggestions={message.suggestions} onClick={onSuggestionClick} />
        )}
      </div>
    </div>
  );
}
